import { Controller } from "@hotwired/stimulus";

type Field = HTMLInputElement | HTMLSelectElement;

/**
 * Show the values of the defaults row as placeholders in the other rows.
 *
 * A field in another row is the same field when its name ends in
 * "-<name>", as a form prefix gives. When the default is empty, the field
 * shows the placeholder it was rendered with again.
 *
 * A select in another row shows the value in its empty choice instead, with
 * the `template` and `fallback` in its data. The select of the defaults row
 * can give the text of each value in `labels`.
 *
 * When the "<name>_required" checkbox of the defaults row is checked, the
 * other rows show no default, their fields are required, and their empty
 * fields get `data-missing`.
 */
export default class InheritedPlaceholdersController extends Controller {
  static override targets = ["defaults"];

  declare readonly defaultsTarget: HTMLElement;

  private fallbacks = new Map<
    HTMLInputElement,
    { placeholder: string; defaultValue: string | undefined }
  >();

  override connect(): void {
    for (const source of this.defaultsTarget.querySelectorAll<Field>(
      "input[type=text][name], select[name]",
    )) {
      this.apply(source);
    }
  }

  update(event: Event): void {
    const target = event.target;
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement
      )
    ) {
      return;
    }
    if (!this.defaultsTarget.contains(target)) {
      this.markMissing(target, this.isRequired(this.fieldName(target)));
      return;
    }
    if (target.type === "checkbox" && target.name.endsWith("_required")) {
      const name = target.name.slice(0, -"_required".length);
      const source = this.defaultsTarget.querySelector<Field>(
        `input[type=text][name="${CSS.escape(name)}"], select[name="${CSS.escape(name)}"]`,
      );
      if (source) this.apply(source);
    } else if (target.type === "text" || target instanceof HTMLSelectElement) {
      this.apply(target);
    }
  }

  private apply(source: Field): void {
    const isRequired = this.isRequired(source.name);
    for (const field of this.element.querySelectorAll<Field>(
      `input[type=text][name$="-${CSS.escape(source.name)}"], ` +
        `select[name$="-${CSS.escape(source.name)}"]`,
    )) {
      if (this.defaultsTarget.contains(field)) continue;
      if (field instanceof HTMLSelectElement) {
        this.applyToSelect(source, field, isRequired);
      } else {
        this.applyToInput(source, field, isRequired);
      }
      this.markMissing(field, isRequired);
    }
  }

  private applyToSelect(
    source: Field,
    select: HTMLSelectElement,
    isRequired: boolean,
  ): void {
    const { inheritedPlaceholdersTemplate: template } = select.dataset;
    const { inheritedPlaceholdersFallback: fallback } = select.dataset;
    const option = select.options[0];
    if (!template || fallback === undefined || option?.value !== "") return;
    const labels = JSON.parse(
      source.dataset.inheritedPlaceholdersLabels ?? "{}",
    ) as Record<string, string>;
    const label = source.value && (labels[source.value] ?? source.value);
    option.text = isRequired
      ? "—"
      : label
        ? template.replace("{value}", label)
        : fallback;
  }

  private applyToInput(
    source: Field,
    input: HTMLInputElement,
    isRequired: boolean,
  ): void {
    const value = isRequired ? "" : source.value.trim();
    // The autosuggest controller sets the placeholder to its default value on
    // blur, so the default value must follow.
    const autosuggest = input.closest<HTMLElement>(
      "[data-controller~='autosuggest']",
    );
    let fallback = this.fallbacks.get(input);
    if (!fallback) {
      fallback = {
        placeholder: input.placeholder,
        defaultValue: autosuggest?.dataset.autosuggestDefaultValueValue,
      };
      this.fallbacks.set(input, fallback);
    }
    input.placeholder = isRequired ? "" : value || fallback.placeholder;
    if (autosuggest && fallback.defaultValue !== undefined) {
      autosuggest.dataset.autosuggestDefaultValueValue = isRequired
        ? ""
        : value || fallback.defaultValue;
    }
  }

  private markMissing(field: Field, isRequired: boolean): void {
    field.required = isRequired;
    field.toggleAttribute("data-missing", isRequired && !field.value.trim());
  }

  private isRequired(name: string): boolean {
    return Boolean(
      this.defaultsTarget.querySelector<HTMLInputElement>(
        `input[type=checkbox][name="${CSS.escape(name)}_required"]`,
      )?.checked,
    );
  }

  /** The name of a field in another row, without its form prefix. */
  private fieldName(field: Field): string {
    return field.name.slice(field.name.lastIndexOf("-") + 1);
  }
}
